import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import React from 'react';
import { history } from 'umi';

import RightContent from '@/components/RightContent';
import TutorialMode from '@/components/TutorialMode';

import layoutSettings from '../config/layout';
import themeSettings, { advanceTheme } from '../config/theme';
// @ts-ignore
import { getMaintenanceStatusApi } from '@/services/maintenance';
import { getUserAccessibleModulesApi } from '@/services/module';
import { getUserApi } from '@/services/user';

import { ThemeProvider } from 'styled-components';
import { request } from './services/interceptor';

import FetchListingContainer from './components/FetchListingContainer';
import TrackingInit from './tracking/TrackingInit';

import type { Settings as LayoutSettings } from '@ant-design/pro-layout';
import type { RunTimeLayoutConfig } from 'umi';

import { getStorageCompanyId, setupCompanyId } from '@/utils/localStorage';
import { ConfigProvider } from 'antd';
import 'antd/dist/reset.css';
import { GlobalStyle } from './Global.style';

if (SENTRY_DSN_SERVER) {
  Sentry.init({
    dsn: SENTRY_DSN_SERVER,
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 0.2,
    environment: GOOGLE_CLOUD_PROJECT,
  });
}

import NoAccessiblePage from './components/ErrorPages/403';
import SystemMaintenanceGate from './components/ErrorPages/SystemMaintenanceGate';

// can view these pages without sso token
const unprotectedPages = [
  '/login',
  '/signup',
  '/contact-support',
  '/resetpassword',
  '/report',
  '/sme-signup',
  '/reg-request',
  '/ga4bg',
  '/trial-application',
];

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.User;
  accessibleModules?: API.CompanyModuleSubscription[];
  collapsed?: boolean;
  maintenanceEnabled?: boolean;
  fetchUserInfo?: () => Promise<API.User | undefined>;
}> {
  const maintenanceEnabled = await getMaintenanceStatusApi()
    .then((res) => Boolean(res?.enabled))
    .catch(() => false);

  const fetchUserInfo = async () => {
    return getUserApi()
      .then((res) => {
        setupCompanyId(res as API.User);
        return res;
      })
      .catch(() => {
        return undefined;
      });
  };

  const { pathname } = history.location;

  if (!unprotectedPages.includes(pathname)) {
    const currentUser = await fetchUserInfo();

    // fetchUserInfo 會執行 setupCompanyId，確保 localStorage 的 companyId 是有效的
    // 這裡重新取得更新後的 companyId
    const companyId = getStorageCompanyId();
    let accessibleModules: API.CompanyModuleSubscription[] = [];

    if (companyId && companyId !== 'undefined') {
      try {
        accessibleModules = await getUserAccessibleModulesApi(companyId);
      } catch (error) {
        console.error('Failed to fetch accessible modules:', error);
        accessibleModules = [];
      }
    }

    return {
      currentUser,
      accessibleModules,
      settings: layoutSettings || {},
      collapsed: false,
      maintenanceEnabled,
    };
  }

  return {
    settings: layoutSettings || {},
    maintenanceEnabled,
  };
}

export function rootContainer(container: React.ReactNode) {
  return (
    // useModel is not exist when component running on rootContainer
    <TrackingInit>
      <ConfigProvider theme={themeSettings}>
        <ThemeProvider theme={{ advanceTheme, ...themeSettings }}>
          <>
            <GlobalStyle />
            {container}
          </>
        </ThemeProvider>
      </ConfigProvider>
    </TrackingInit>
  );
}

export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  const { collapsed = false } = initialState || {};
  return {
    rightContentRender: () => <RightContent />,
    disableContentMargin: false,
    onPageChange: () => {
      window.scrollTo({
        top: 0,
      });
      const { pathname } = history.location;
      if (!unprotectedPages.includes(pathname) && !initialState?.currentUser?.id) {
        getUserApi().catch((err) => console.log(err));
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-shadow
    onCollapse: (collapsed) => setInitialState({ ...initialState, collapsed }),
    childrenRender: (children) => {
      return (
        <SystemMaintenanceGate>
          <FetchListingContainer>
            <TutorialMode />
            {children}
          </FetchListingContainer>
        </SystemMaintenanceGate>
      );
    },
    collapsed,
    // force change the menu type when collapsed changed
    menu: collapsed
      ? {
          type: 'sub',
        }
      : { type: 'group' },
    // custom 403 page
    unAccessible: <NoAccessiblePage />,
    ...initialState?.settings,
  };
};

export { request };
